// /users/sign_in — Inertia replacement for the Devise default ERB form.
// Three sign-in paths share this single screen:
//
//   1. Email + password (the standard Devise database_authenticatable
//      flow). useForm POSTs to /users/sign_in. On success the server
//      redirects to /dashboard. If the user owes a 2nd factor the
//      server re-renders this page with `mfa_step="challenge"`.
//
//   2. Usernameless passkey shortcut. "Sign in with passkey" → POSTs
//      /users/sign_in/passkey/options to fetch assertion options, runs
//      navigator.credentials.get(...), POSTs the result to
//      /users/sign_in/passkey/verify. Server signs the user in and
//      stamps mfa_passed_at (passkey IS the second factor).
//
//   3. Inline MFA challenge. When the SignIn page is rendered with
//      mfa_step="challenge", we swap the password panel for the shared
//      <MfaChallenge> component AND show a green "Password accepted"
//      success banner above it. The challenge UI POSTs to the existing
//      /portal/mfa/challenge/* endpoints which redirect to /dashboard
//      on success.
//
// Layout: standalone centered card on a dark backdrop with a hero
// LogoMark on top — the user is not signed in yet so we don't render
// AppShell (no sidebar, no company switcher). Same chrome is used by
// the standalone /portal/mfa/challenge page so the auth flow looks
// continuous if the user lands on either entrypoint.

import { useState } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Check, Eye, EyeOff, KeyRound, Lock, Mail } from "lucide-react"
import { LogoMark } from "@/components/layout/logo-mark"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { MfaChallenge, type MfaChallengeOptions } from "@/components/domain/mfa-challenge"
import { verifyPasskey } from "@/lib/webauthn"
import { cn } from "@/lib/cn"

type FlashShape = { notice?: string | null, alert?: string | null }

type Props = {
  mfa_step:        "challenge" | null
  mfa_options:     MfaChallengeOptions | null
  passkey_options: Record<string, unknown> | null
  flash:           FlashShape
}

export default function SignIn({ mfa_step, mfa_options, passkey_options, flash }: Props) {
  // usePage flash falls back to the page-level flash prop — Inertia
  // shares one flash hash globally on portal pages, but Devise auth
  // routes don't go through Portal::ApplicationController#inertia_share.
  const page = usePage<{ flash?: FlashShape }>()
  const sharedFlash = page.props.flash || {}
  const showFlash: FlashShape = {
    notice: flash?.notice ?? sharedFlash.notice ?? null,
    alert:  flash?.alert  ?? sharedFlash.alert  ?? null,
  }

  const isMfaStep = mfa_step === "challenge" && mfa_options !== null

  // Wider container on the MFA step so the split-pane card has room to breathe.
  const containerWidth = isMfaStep ? "max-w-2xl" : "max-w-md"

  const heading  = isMfaStep ? "Finish signing in" : "Sign in to Valet"
  const subhead  = isMfaStep
    ? "Choose a verification method to confirm it\u2019s you."
    : "Welcome back."

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-bg px-6 py-12 text-app-text">
      <div className={`w-full ${containerWidth} space-y-8`}>
        <header className="flex flex-col items-center gap-5">
          <LogoMark size="hero" />
          <div className="text-center">
            <h1 className="text-3xl font-semibold tracking-tight">{heading}</h1>
            <p className="mt-2 text-sm text-app-muted">{subhead}</p>
          </div>
        </header>

        {showFlash.notice && (
          <div className="rounded-control border border-status-success/30 bg-status-success/10 px-4 py-2 text-sm text-status-success">
            {showFlash.notice}
          </div>
        )}
        {showFlash.alert && (
          <div className="rounded-control border border-status-error/30 bg-status-error/10 px-4 py-2 text-sm text-status-error">
            {showFlash.alert}
          </div>
        )}

        {isMfaStep && <PasswordAcceptedBanner />}

        {isMfaStep && mfa_options
          ? <MfaChallenge {...mfa_options} />
          : <PasswordCard passkeyOptions={passkey_options} />}

        <p className="text-center text-xs text-app-subtle">
          Trouble signing in?{" "}
          <a href="mailto:support@valet.red" className="text-brand-red hover:underline">Contact support</a>
        </p>
      </div>
    </div>
  )
}

// Green pill banner shown above the MFA card on the post-password path.
// Signals that the password is accepted and the only remaining gate is
// the second factor — reduces the "wait, am I locked out?" anxiety when
// the page suddenly swaps from email/password to a factor picker.
function PasswordAcceptedBanner() {
  return (
    <div className="flex items-center gap-3 rounded-control border border-status-success/30 bg-status-success/10 px-4 py-3 text-sm text-status-success">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-status-success text-white">
        <Check size={14} strokeWidth={3} />
      </span>
      <span className="text-app-text">
        Password accepted. One final verification step remains.
      </span>
    </div>
  )
}

function PasswordCard({ passkeyOptions }: { passkeyOptions: Record<string, unknown> | null }) {
  const form = useForm({ user: { email: "", password: "", remember_me: "0" } })
  const [showPassword, setShowPassword] = useState(false)

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    form.post("/users/sign_in")
  }

  const rememberChecked = form.data.user.remember_me === "1"

  return (
    <Card className="border-brand-red/40 p-6 shadow-redGlow">
      <form onSubmit={submit} className="space-y-4">
        <label className="block text-sm">
          <span className="block font-medium text-app-text">Email</span>
          <div className="relative mt-1">
            <Mail
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-app-subtle"
            />
            <Input
              type="email"
              placeholder="Enter your email"
              value={form.data.user.email}
              onChange={(e) => form.setData("user", { ...form.data.user, email: e.target.value })}
              autoComplete="username webauthn"
              autoFocus
              required
              className="w-full pl-9"
            />
          </div>
        </label>

        <label className="block text-sm">
          <span className="block font-medium text-app-text">Password</span>
          <div className="relative mt-1">
            <Lock
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-app-subtle"
            />
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={form.data.user.password}
              onChange={(e) => form.setData("user", { ...form.data.user, password: e.target.value })}
              autoComplete="current-password"
              required
              className="w-full pl-9 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-control p-1.5 text-app-subtle transition hover:text-app-text"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </label>

        <label className="flex cursor-pointer items-center gap-2 text-sm text-app-muted">
          <span
            className={cn(
              "flex h-4 w-4 items-center justify-center rounded-sm border transition",
              rememberChecked
                ? "border-brand-red bg-brand-red text-white"
                : "border-border bg-white/5",
            )}
          >
            {rememberChecked && <Check size={12} strokeWidth={3} />}
          </span>
          <input
            type="checkbox"
            checked={rememberChecked}
            onChange={(e) => form.setData("user", { ...form.data.user, remember_me: e.target.checked ? "1" : "0" })}
            className="sr-only"
          />
          Remember me
        </label>

        <Button
          type="submit"
          className="w-full py-3 text-base font-semibold hover:shadow-redGlow"
          disabled={form.processing}
        >
          {form.processing ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs text-app-subtle">
        <span className="h-px flex-1 bg-border" />
        <span>or</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <PasskeyShortcut options={passkeyOptions} />

      <p className="mt-6 text-center text-sm">
        <a href="/users/password/new" className="text-brand-red hover:underline">Forgot your password?</a>
      </p>
    </Card>
  )
}

function PasskeyShortcut({ options }: { options: Record<string, unknown> | null }) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function go() {
    setSubmitting(true)
    setError(null)
    try {
      // Always re-fetch options from the server. The initial-render
      // options can be stale (challenge already consumed by an earlier
      // failed attempt), and re-fetching also rotates the challenge.
      const res = await fetch("/users/sign_in/passkey/options", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken() },
        credentials: "same-origin",
      })
      if (!res.ok) throw new Error("Couldn't start passkey sign-in.")
      const fresh = await res.json()
      const credential = await verifyPasskey(fresh as Parameters<typeof verifyPasskey>[0])

      const verifyRes = await fetch("/users/sign_in/passkey/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": csrfToken() },
        credentials: "same-origin",
        body: JSON.stringify({ credential }),
      })
      const payload = await verifyRes.json()
      if (!verifyRes.ok) throw new Error(payload.error || "Passkey sign-in failed.")
      window.location.assign(payload.redirect_to || "/dashboard")
    } catch (e) {
      setError((e as Error).message)
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-2">
      {error && <p className="text-sm text-status-error">{error}</p>}
      <Button
        type="button"
        variant="ghost"
        onClick={go}
        disabled={submitting || !options}
        className="w-full gap-2 py-3 text-sm font-medium"
      >
        <KeyRound size={16} />
        {submitting ? "Waiting for browser…" : "Sign in with passkey"}
      </Button>
    </div>
  )
}

function csrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? (meta.getAttribute("content") || "") : ""
}
